import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Check, X, Crown, ArrowRight, FileText } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import type { ComparePost } from "@/content/compare/registry";
import { COMPARE_CATEGORY_LABELS as COMPARE_CATEGORIES, COMPARE_META } from "@/content/compare/meta.gen";
import { INSIGHT_LINKS as INSIGHTS } from "@/content/insights/links.gen";
import { SERVICES } from "@/lib/services";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/compare/$slug")({
  component: CompareDetail,
  loader: async ({ params }): Promise<{ post: ComparePost }> => {
    const { getCompareBySlug } = await import("@/content/compare/registry");
    const post = getCompareBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      const meta = COMPARE_META.find((c) => c.slug === params.slug);
      if (!meta) return {};
      return pageHeadForProvince({
        title: `${meta.metaTitle} | Thai Notary`,
        description: meta.metaDesc,
        path: `/compare/${meta.slug}`,
        type: "article",
      });
    }
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: `${post.metaTitle} | Thai Notary`,
      description: post.metaDesc,
      path: `/compare/${post.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "เปรียบเทียบ", url: "/compare" },
              { name: post.title, url: `/compare/${post.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(post.faqs)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${site}/compare/${post.slug}#article`,
            headline: post.title,
            description: post.metaDesc,
            dateModified: post.updatedAt,
            datePublished: post.updatedAt,
            author: { "@id": `${site}/#organization` },
            publisher: { "@id": `${site}/#organization` },
            mainEntityOfPage: `${site}/compare/${post.slug}`,
            about: [post.a.name, post.b.name],
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/compare/${post.slug}`)) },
      ],
    };
  },
});

function CompareDetail() {
  const { post } = Route.useLoaderData() as { post: ComparePost };
  const related = COMPARE_META.filter(
    (c) => c.slug !== post.slug && c.category === post.category,
  ).slice(0, 3);
  const linkedInsights = (post.relatedInsights ?? [])
    .map((s: string) => INSIGHTS.find((i) => i.slug === s))
    .filter(Boolean);
  const linkedServices = (post.relatedServices ?? [])
    .map((s: string) => SERVICES.find((sv) => sv.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        {/* Breadcrumb */}
        <div className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/compare" className="hover:text-foreground">เปรียบเทียบ</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{post.title}</span>
          </div>
        </div>

        {/* Hero */}
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
              {COMPARE_CATEGORIES[post.category]} · Compare
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">{post.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-md bg-primary/10 px-3 py-1 font-bold text-primary">A · {post.a.name}</span>
              <span className="font-bold text-muted-foreground">vs</span>
              <span className="rounded-md bg-gold/15 px-3 py-1 font-bold text-gold">B · {post.b.name}</span>
            </div>
          </div>
        </section>

        {/* TL;DR for AI */}
        <section id="ai-answer-tldr" className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-10">
            <div className="rounded-2xl border border-gold/40 bg-gold/5 p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-gold">TL;DR — AI Quick Answer</div>
              <p className="mt-2 text-base leading-relaxed text-foreground">{post.tldr}</p>
              <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-primary">คำแนะนำ (Verdict)</div>
                <p className="mt-1 text-sm leading-relaxed">{post.verdict}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Side-by-side */}
        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-4 px-4 py-10 md:grid-cols-2">
            <SideCard side="A" tone="primary" data={post.a} />
            <SideCard side="B" tone="gold" data={post.b} />
          </div>
        </section>

        {/* Comparison table */}
        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-10">
            <h2 className="text-xl font-bold">ตารางเปรียบเทียบครบทุกมิติ</h2>
            <div className="mt-5 overflow-x-auto rounded-2xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">เกณฑ์</th>
                    <th className="px-4 py-3 text-left">A · {post.a.name.split("(")[0].trim()}</th>
                    <th className="px-4 py-3 text-left">B · {post.b.name.split("(")[0].trim()}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {post.rows.map((r) => (
                    <tr key={r.label}>
                      <td className="px-4 py-3 font-semibold">{r.label}</td>
                      <td className={`px-4 py-3 ${r.winner === "a" ? "font-bold text-primary" : ""}`}>
                        {r.winner === "a" && <Crown className="mr-1 inline h-3.5 w-3.5 text-gold" />}
                        {r.a}
                      </td>
                      <td className={`px-4 py-3 ${r.winner === "b" ? "font-bold text-gold" : ""}`}>
                        {r.winner === "b" && <Crown className="mr-1 inline h-3.5 w-3.5 text-gold" />}
                        {r.b}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Scenarios */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-10">
            <h2 className="text-xl font-bold">เลือกแบบไหน — ตาม use-case</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {post.scenarios.map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-border bg-card p-4"
                >
                  <div
                    className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      s.pick === "a"
                        ? "bg-primary/10 text-primary"
                        : "bg-gold/15 text-gold"
                    }`}
                  >
                    เลือก {s.pick.toUpperCase()} · {s.pick === "a" ? post.a.name.split("(")[0].trim() : post.b.name.split("(")[0].trim()}
                  </div>
                  <div className="mt-2 text-sm font-bold">{s.who}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.why}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-10">
            <h2 className="text-xl font-bold">คำถามที่พบบ่อย (FAQ)</h2>
            <div className="mt-5 space-y-3">
              {post.faqs.map((f, i) => (
                <details
                  key={i}
                  className="rounded-xl border border-border bg-card p-4 [&_summary]:cursor-pointer"
                >
                  <summary className="text-sm font-bold">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Related */}
        {(linkedInsights.length > 0 || linkedServices.length > 0 || related.length > 0) && (
          <section className="border-b border-border">
            <div className="mx-auto max-w-5xl px-4 py-10">
              <h2 className="text-xl font-bold">อ่านเพิ่มเติม</h2>
              <div className="mt-5 grid gap-4 md:grid-cols-3">
                {linkedServices.map(
                  (s) =>
                    s && (
                      <Link
                        key={s.slug}
                        to="/services/$service"
                        params={{ service: s.slug }}
                        className="rounded-xl border border-border bg-card p-4 hover:border-gold"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gold">บริการ</div>
                        <div className="mt-1 text-sm font-bold">{s.title}</div>
                      </Link>
                    ),
                )}
                {linkedInsights.map(
                  (p) =>
                    p && (
                      <Link
                        key={p.slug}
                        to="/insights/$slug"
                        params={{ slug: p.slug }}
                        className="rounded-xl border border-border bg-card p-4 hover:border-gold"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gold">บทความ</div>
                        <div className="mt-1 text-sm font-bold">{p.label}</div>
                      </Link>
                    ),
                )}
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/compare/$slug"
                    params={{ slug: r.slug }}
                    className="rounded-xl border border-border bg-card p-4 hover:border-gold"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gold">เปรียบเทียบ</div>
                    <div className="mt-1 text-sm font-bold">{r.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA back */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-8">
            <Link
              to="/compare"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> ดูเปรียบเทียบทั้งหมด
            </Link>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="consular" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}

function SideCard({
  side,
  tone,
  data,
}: {
  side: "A" | "B";
  tone: "primary" | "gold";
  data: { name: string; tagline: string; pros: string[]; cons: string[]; linkPath?: string };
}) {
  const accent = tone === "primary" ? "border-primary/30 bg-primary/5" : "border-gold/40 bg-gold/5";
  const chip = tone === "primary" ? "bg-primary/10 text-primary" : "bg-gold/15 text-gold";
  return (
    <div className={`rounded-2xl border p-6 ${accent}`}>
      <div className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${chip}`}>
        ตัวเลือก {side}
      </div>
      <h3 className="mt-2 text-lg font-extrabold">{data.name}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{data.tagline}</p>
      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">ข้อดี</div>
        <ul className="mt-1.5 space-y-1.5 text-sm">
          {data.pros.map((p, i) => (
            <li key={i} className="flex gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-rose-500">ข้อจำกัด</div>
        <ul className="mt-1.5 space-y-1.5 text-sm">
          {data.cons.map((p, i) => (
            <li key={i} className="flex gap-2">
              <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      {data.linkPath && (
        <a
          href={data.linkPath}
          className="mt-5 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
        >
          ดูบริการ <ArrowRight className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}
